import {
	ColumnModeTypeEnum,
	SuperGridColDef,
	createBuilder,
	GetConfigType,
	TransformRowsParams,
	flattenObject,
	SelectCellRenderer,
	DateCellRenderer,
	DashboardFrame,
} from '@aleph/supergrid';
import {
	AppInstallsIcon,
	AwarenessIcon,
	ConversionsIcon,
	VideoViewsIcon,
	TrafficIcon,
	alephToast,
	NotificationToast,
	NotificationType,
	MoneyCellEditor,
	ExpandHeaderCellRenderer,
	IndexHeaderRenderer,
	NameHeaderRenderer,
	CustomHeader,
	SelectCellEditor,
	cn,
	NoValuePlaceholder,
} from '@aleph/shared-components';
import StatusCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/status';
import RedditNameCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/name/container';
import SettingsCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/settings';
import SettingsHeader from 'wb-reddit/app/routes/superGrid/cellRenderers/settingsHeader';
import {
	getFundingInstrumentOptions,
	RedditBreakdowns, RedditColors, RedditLevel,
} from 'wb-reddit/app/routes/superGrid/utils';
import {
	GROUP_COLUMN_DEFINITIONS,
	transformGroupTargeting,
} from 'wb-reddit/app/routes/superGrid/columnDefinitions/group';
import StatCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/stat';
import AD_COLUMN_DEFINITIONS from 'wb-reddit/app/routes/superGrid/columnDefinitions/ad';
import { editableOnLevel, editableOnThisLevel } from 'wb-reddit/app/routes/superGrid/helpers/index';
import {
	AdSuperGridListDocument,
	AdSuperGridListQueryFlatCreative,
	AdSuperGridListQueryItems,
	CampaignObjective,
	CampaignSuperGridListDocument,
	ClientSuperGridListDocument,
	CreativeType,
	EventTrackerType,
	GroupSuperGridListDocument,
} from 'wb-reddit/graphql/generated';
import RedditCellRendererWrapper from 'wb-reddit/app/routes/superGrid/cellRenderers/wrapper';
import ClientSettingsCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/clientSettings';
import ClientSettingsHeaderRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/clientSettingsHeader';
import { divideCurrencyFields } from 'wb-reddit/app/routes/superGrid/common/constants';
import {
	adHeaderNames,
	campaignHeaderNames,
	groupHeaderNames,
} from 'wb-reddit/app/routes/superGrid/columnDefinitions/headerNames';
import AdAccountSettingsCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/adAccountSettings';
import IndexCellRenderer from 'wb-reddit/app/common/components/indexCell';
import { getRedditTargetingsByIds } from 'wb-reddit/app/routes/superGrid/targeting/utils';
import { flatMap, uniq } from 'lodash';
import { captureException } from '@sentry/react';
import { ApolloClient } from '@apollo/client';
import { getRedditBreakdownQuery } from 'wb-reddit/app/routes/superGrid/view/utils';
import { LEVEL_ID_FILTER } from 'wb-reddit/app/routes/superGrid/actionRow/filters/utils';
import {
	HeaderClassParams,
	SortModelItem,
	IServerSideGetRowsParams,
	ICellRendererParams,
	SuppressKeyboardEventParams,
	ValueSetterParams,
} from 'ag-grid-community';
import MoneyCellRenderer from 'wb-reddit/src/app/routes/superGrid/cellRenderers/money';
import NameCellEditor from 'wb-reddit/src/app/common/components/autoNaming/autoNamingEditor';

const transformRedditRow = (
	row: RedditRows[number],
	requestLevel: RedditLevel,
	breakdown: RedditBreakdowns = RedditBreakdowns.ENTITIES,
) => {
	if (row.supergridIsNoDataRow || row.supergridIsCreateRow) {
		return flattenObject({
			...row,
			level:     requestLevel,
			isLastRow: row.superGridIsLastRow,
		});
	}
	const newRow = {
		...row,
		...(
			row.supergridLevel !== RedditLevel.BREAKDOWN
				? row.flatStats?.[0] : {}
		),
		...(transformGroupTargeting(
			row.supergridLevel === RedditLevel.GROUP
				? row?.groupTargeting ?? {} : {},
		)),
		...(
			row.supergridLevel === RedditLevel.AD
				? row?.flatCreative : {}
		),
		...(
			row.supergridLevel === RedditLevel.CAMPAIGN
				? { fundingInstrument: row.fundingInstrument ? row.fundingInstrument?.fundingInstrumentId : null }
				: {}
		),
		isDashboardView:          row.supergridLevel === RedditLevel.AD_ACCOUNT && row?.clientPinned,
		supergridIsDashboardView: row.supergridLevel === RedditLevel.AD_ACCOUNT && row?.clientPinned,
		level:                    requestLevel,
		isLastRow:                row.superGridIsLastRow,
		// eslint-disable-next-line no-underscore-dangle
		id:                       row?.__typename === 'Stats' ? `${row?.id}_${performance.now()}` : row?.id,
		...(
			row.supergridLevel === RedditLevel.AD
				? [...Array(6).keys()]
					.filter(index => row?.flatCreative?.flatCreativeContent?.[index])
					.reduce(
						(creativeContentMediaUrls, index) => ({
							...creativeContentMediaUrls,
							[`creativeContentMediaUrl${index}`]: {
								mediaUrl: row?.flatCreative
									?.flatCreativeContent?.[index]?.creativeContentMediaUrl,
								mediaThumbnail: row?.flatCreative?.creativeType === CreativeType.Carousel
									? row?.flatCreative?.flatCreativeContent?.[index]?.creativeContentMediaUrl
									: row?.flatCreative?.creativeThumbnailUrl === 'default' ? undefined : row?.flatCreative?.creativeThumbnailUrl,
							},
							[`creativeContentDestinationUrl${index}`]: row?.flatCreative?.flatCreativeContent?.[index]?.creativeContentDestinationUrl,
							[`creativeContentDisplayUrl${index}`]:     row?.flatCreative?.flatCreativeContent?.[index]?.creativeContentDisplayUrl,
							[`creativeContentCaption${index}`]:        row?.flatCreative?.flatCreativeContent?.[index]?.creativeContentCaption,
							[`creativeContentCallToAction${index}`]:   row?.flatCreative?.flatCreativeContent?.[index]?.creativeContentCallToAction,
						}),
						{},
					)
				: {}
		),
		isBreakdownParent:    breakdown !== RedditBreakdowns.ENTITIES,
		creativeThumbnailUrl: undefined as AdSuperGridListQueryFlatCreative['creativeThumbnailUrl'],
		adClickEventTrackers: undefined as AdSuperGridListQueryItems['adEventTrackers'] | undefined,
		adViewEventTrackers:  undefined as AdSuperGridListQueryItems['adEventTrackers'] | undefined,
	};

	if (row.supergridLevel === RedditLevel.AD) {
		newRow.creativeThumbnailUrl = row?.flatCreative?.creativeThumbnailUrl === 'default' ? undefined : row?.flatCreative?.creativeThumbnailUrl;
		newRow.adClickEventTrackers = row?.adEventTrackers
			?.filter(tracker => tracker?.type === EventTrackerType.Click);
		newRow.adViewEventTrackers = row?.adEventTrackers
			?.filter(tracker => tracker?.type === EventTrackerType.View);
	}

	return flattenObject(newRow);
};

const appendTargetingLabels = async (rows, requestLevel: RedditLevel) => {
	if (requestLevel !== RedditLevel.GROUP) {
		return rows;
	}

	// we can combine locations & excluded locations into one query as it's the same endpoint
	const locationIds = uniq(flatMap(
		rows,
		row => [...row?.groupTargeting?.locations ?? [], ...row?.groupTargeting?.locationsExcluded ?? []],
	));

	// we can combine communities & excluded communities into one query as it's the same endpoint
	const communityIds = uniq(flatMap(
		rows,
		row => [...row?.groupTargeting?.communities ?? [], ...row?.groupTargeting?.communitiesExcluded ?? []],
	));

	// we can combine custom audiences & excluded custom audiences into one query as it's the same endpoint
	const audienceIds = uniq(flatMap(
		rows,
		row => [...row?.groupTargeting?.customAudiences ?? [], ...row?.groupTargeting?.customAudiencesExcluded ?? []],
	));

	const interestIds = uniq(flatMap(
		rows,
		row => row?.groupTargeting?.interests ?? [],
	));

	const carrierIds = uniq(flatMap(
		rows,
		row => row?.groupTargeting?.carriers ?? [],
	));

	try {
		const {
			locations,
			communities,
			audiences,
			interests,
			carriers,
		} = await getRedditTargetingsByIds({
			locationIds,
			communityIds,
			interestIds,
			carrierIds,
			audienceIds,
		});

		return rows.map(row => ({
			...row,
			locations: locations?.filter(
				location => row?.groupTargeting?.locations?.includes(location?.value),
			),
			locationsExcluded: locations?.filter(
				location => row?.groupTargeting?.locationsExcluded?.includes(location?.value),
			),

			communities: communities?.filter(
				community => row?.groupTargeting?.communities?.includes(community?.value),
			),
			communitiesExcluded: communities?.filter(
				community => row?.groupTargeting?.communitiesExcluded?.includes(community?.value),
			),

			customAudiences: audiences?.filter(
				customAudience => row?.groupTargeting?.customAudiences?.includes(customAudience?.value),
			),
			customAudiencesExcluded: audiences?.filter(
				customAudience => row?.groupTargeting?.customAudiencesExcluded?.includes(customAudience?.value),
			),

			interests: interests?.filter(
				interest => row?.groupTargeting?.interests?.includes(interest?.value),
			),

			carriers: carriers?.filter(
				carrier => row?.groupTargeting?.carriers?.includes(carrier?.value),
			),
		}));
	} catch (e) {
		captureException(e);

		alephToast(
			<NotificationToast
				type={NotificationType.Error}
				title="There was an error while getting targeting data."
				description="Some groups may have corrupt targeting settings."
			/>,
		);
	}

	return rows;
};

export const NAME_COL_DEF: SuperGridColDef = {
	field:                 'name',
	columnMode:            ColumnModeTypeEnum.BOTH,
	headerComponent:       NameHeaderRenderer,
	cellRenderer:          RedditNameCellRenderer,
	cellEditor:            NameCellEditor,
	editable:              editableOnThisLevel,
	lockPosition:          true,
	headerComponentParams: params => {
		const columns = params.columnApi?.getColumns() ?? [];

		return {
			showLabel: columns[columns.indexOf(params.column) - 1]?.colDef?.field !== params?.column?.colDef?.field,
		};
	},
	cellEditorParams: params => ({
		...params,
		rowHeight: params.node.rowHeight,
	}),
	headerClass: (params: HeaderClassParams) => {
		const columns = params.columnApi?.getColumns() ?? [];

		const { column } = params;

		return cn(
			'border-r-0 border-b border-b-gray-300',
			{
				'!overflow-visible border-l !w-auto': columns
					&& columns[column ? columns.indexOf(column) - 1 : 0]
						?.getColDef()?.field !== column?.getColDef()?.field,
			},
		);
	},
	cellStyle: {
		borderRightWidth: 1,
		borderLeftWidth:  1,
		borderRightColor: 'hsl(var(--gray-300))',
		borderLeftColor:  'hsl(var(--gray-300))',
		padding:          0,
	},
};

export const AD_ACCOUNT_COL_DEF: SuperGridColDef[] = [
	{
		field:        'id',
		rowGroup:     true,
		elasticChild: 'settingsElastic',
	},
	{
		field:        'clientCreatedAt',
		cellRenderer: DateCellRenderer,
		elasticChild: 'settingsElastic',
	},
];

const CAMPAIGN_OBJECTIVE_OPTIONS = [
	{
		icon: <VideoViewsIcon fill="hsl(var(--blue-500))"
			height={16}
			width={16}
		/>,
		value: CampaignObjective.Impressions,
		label: 'Brand Awareness and Reach',
	},
	{
		icon: <ConversionsIcon fill="hsl(var(--blue-500))"
			height={16}
			width={16}
		/>,
		value: CampaignObjective.Clicks,
		label: 'Traffic',
	},
	{
		icon: <AwarenessIcon fill="hsl(var(--blue-500))"
			height={16}
			width={16}
		/>,
		value: CampaignObjective.Conversions,
		label: 'Conversions',
	},
	{
		icon: <AppInstallsIcon fill="hsl(var(--blue-500))"
			height={16}
			width={16}
		/>,
		value: CampaignObjective.VideoViewableImpressions,
		label: 'Video Views',
	},
	{
		icon: <TrafficIcon fill="hsl(var(--blue-500))"
			height={16}
			width={16}
		/>,
		value: CampaignObjective.AppInstalls,
		label: 'App Installs',
	},
];

type GetRedditVariables = (useBreakdown: boolean) => (
	level: RedditLevel,
	filterModel: SortModelItem[],
) => any;

const getDefaultCreateData = (
	dataSourceParams: IServerSideGetRowsParams,
	readOnly?: boolean,
	breakdown: RedditBreakdowns = RedditBreakdowns.ENTITIES,
) => {
	if (breakdown !== RedditBreakdowns.ENTITIES || readOnly || !(dataSourceParams.parentNode.level >= 0)) {
		return null;
	}

	return {
		status:   'DRAFT',
		isCreate: true,
	};
};

const getNoDataRow = (
	breakdown:RedditBreakdowns = RedditBreakdowns.ENTITIES,
) => (
	breakdown !== RedditBreakdowns.ENTITIES ? {
		name:        'No data',
		isNoDataRow: true,
	} : null
);

const getRedditVariables = (
	level: RedditLevel,
	parentId: string | undefined | null,
	filterMode: SortModelItem[],
	dataSourceParams: IServerSideGetRowsParams,
	cursor: string | null | undefined,
	limit: number | null | undefined,
	getVariables?: GetRedditVariables,
	breakdown: RedditBreakdowns = RedditBreakdowns.ENTITIES,
) => {
	const useBreakdown = breakdown
    && breakdown !== RedditBreakdowns.ENTITIES
    && dataSourceParams?.parentNode?.level !== -1;
	const variables = getVariables?.(useBreakdown)(level, filterMode);
	const parentLevel = dataSourceParams?.parentNode?.data?.supergridLevel ?? undefined;
	const parentFilter = !parentLevel ? {}
		: { [LEVEL_ID_FILTER[parentLevel]]: [{ value: [parentId ?? ''] }] };

	return {
		...variables,
		filters: {
			...variables?.filters,
			...(parentFilter),
		},
		cursor,
		limit: limit ?? 100,
	};
};

export const CONFIG_BASE = createBuilder()
	.metaData<{ cursor?: string | null }>()
	.context<{
	client: ApolloClient<any>,
	breakdown?: RedditBreakdowns,
	readOnly?: boolean,
	getVariables?: GetRedditVariables
}>()
	.addLevel({ name: RedditLevel.AD_ACCOUNT })
	.addLevel({ name: RedditLevel.CAMPAIGN })
	.addLevel({ name: RedditLevel.GROUP })
	.addLevel({ name: RedditLevel.AD })
	.addLevel({ name: RedditLevel.BREAKDOWN })
	.getDataForLevel(RedditLevel.AD_ACCOUNT, async ({
		ctx, dataSourceParams, level, parentId, filteredSortModel, previousRequestMetaData, limit,
	}) => {
		const { data } = await ctx.client.query({
			query:     ClientSuperGridListDocument,
			variables: getRedditVariables(
				level,
				parentId,
				filteredSortModel,
				dataSourceParams,
				previousRequestMetaData?.cursor,
				limit,
				ctx.getVariables,
				ctx.breakdown,
			),
		});

		return {
			data:      data.entities.items ?? [],
			metaData:  { cursor: data.entities.cursor },
			noDataRow: getNoDataRow(ctx.breakdown),
		};
	})
	.getDataForLevel(RedditLevel.CAMPAIGN, async ({
		ctx, dataSourceParams, level, parentId, filteredSortModel, previousRequestMetaData, limit,
	}) => {
		const { data } = await ctx.client.query({
			query:     CampaignSuperGridListDocument,
			variables: getRedditVariables(
				level,
				parentId,
				filteredSortModel,
				dataSourceParams,
				previousRequestMetaData?.cursor,
				limit,
				ctx.getVariables,
				ctx.breakdown,
			),
		});

		return {
			data:      data.entities.items ?? [],
			metaData:  { cursor: data.entities.cursor },
			createRow: getDefaultCreateData(dataSourceParams, ctx.readOnly, ctx.breakdown),
			noDataRow: getNoDataRow(ctx.breakdown),
		};
	})
	.getDataForLevel(RedditLevel.GROUP, async ({
		ctx, dataSourceParams, level, parentId, filteredSortModel, previousRequestMetaData, limit,
	}) => {
		const { data } = await ctx.client.query({
			query:     GroupSuperGridListDocument,
			variables: getRedditVariables(
				level,
				parentId,
				filteredSortModel,
				dataSourceParams,
				previousRequestMetaData?.cursor,
				limit,
				ctx.getVariables,
				ctx.breakdown,
			),
		});

		return {
			data:      data.entities.items ?? [],
			metaData:  { cursor: data.entities.cursor },
			createRow: getDefaultCreateData(dataSourceParams, ctx.readOnly, ctx.breakdown),
			noDataRow: getNoDataRow(ctx.breakdown),
		};
	})
	.getDataForLevel(RedditLevel.AD, async ({
		ctx, dataSourceParams, level, parentId, filteredSortModel, previousRequestMetaData, limit,
	}) => {
		const { data } = await ctx.client.query({
			query:     AdSuperGridListDocument,
			variables: getRedditVariables(
				level,
				parentId,
				filteredSortModel,
				dataSourceParams,
				previousRequestMetaData?.cursor,
				limit,
				ctx.getVariables,
				ctx.breakdown,
			),
		});

		return {
			data:      data.entities.items ?? [],
			metaData:  { cursor: data.entities.cursor },
			createRow: getDefaultCreateData(dataSourceParams, ctx.readOnly, ctx.breakdown),
			noDataRow: getNoDataRow(ctx.breakdown),
		};
	})
	.getDataForLevel(RedditLevel.BREAKDOWN, async ({
		ctx, dataSourceParams, level, parentId, filteredSortModel, previousRequestMetaData, limit,
	}) => {
		const { data } = await ctx.client.query({
			query:     getRedditBreakdownQuery(ctx.breakdown)!,
			variables: getRedditVariables(
				level,
				parentId,
				filteredSortModel,
				dataSourceParams,
				previousRequestMetaData?.cursor,
				limit,
				ctx.getVariables,
				ctx.breakdown,
			),
		});

		return {
			data:      data?.entities?.items ?? [],
			metaData:  { cursor: data?.entities?.cursor },
			noDataRow: getNoDataRow(ctx.breakdown),
		};
	});

const CONFIG_BASE_CONFIG = CONFIG_BASE.getConfig();

type ConfigBuilderType = GetConfigType<typeof CONFIG_BASE_CONFIG>;

export type RedditRows = TransformRowsParams<
ConfigBuilderType['levelNames'],
ConfigBuilderType['queriedData'],
ConfigBuilderType['context']
>['rows'];

export type RedditRow = RedditRows[number];

export const transformRedditRows = async (
	rows: RedditRows,
	requestLevel: RedditLevel,
	breakdown: RedditBreakdowns = RedditBreakdowns.ENTITIES,
// eslint-disable-next-line @typescript-eslint/return-await
) => await appendTargetingLabels(
	rows?.map(row => transformRedditRow(row, requestLevel, breakdown)) ?? [],
	requestLevel,
);

export const REDDIT_SUPERGRID_CONFIG = CONFIG_BASE
	.transformRows(async ({ ctx, rows, level }) => {
		const result = await transformRedditRows(rows, level, ctx.breakdown);

		return result;
	})
	.options({
		showHiddenNameColumns:  true,
		emptyCellsCellRenderer: () => <NoValuePlaceholder />,
		cellRendererWrapper:    RedditCellRendererWrapper,
		expandSkipSpacing:      (params) => params.data?.level === RedditLevel.BREAKDOWN,
		expandColDef: 			       {
			field:              'expand',
			lockPosition:       true,
			headerComponent:    ExpandHeaderCellRenderer,
			cellRendererParams: {
				onToggle: (e, params) => {
					if (
						params.node?.data?.isCreate || (
							params.node?.data?.level === RedditLevel.AD
                        && !params.node?.data?.isBreakdownParent
						)
                    || params.node?.data?.level === RedditLevel.BREAKDOWN
					) {
						return;
					}

					params.node.setExpanded(!params.node.expanded, e);
					params.api.refreshCells();
				},
				Component: (params) => {
					const isDashboardView = params.data?.supergridIsDashboardView && !params.node.expanded;
					const colors = params.context?.colors;
					const nodeLevelName = params.node?.data?.supergridLevel ?? '';

					return 	(
						<>
							{isDashboardView && <div className="pt-[1px]">
								<DashboardFrame className={`border-l border-l-[${colors[nodeLevelName]}]`} />
							</div>
							}
						</>
					);
				},
			},
			headerClass: 'border-r-0 border-b border-b-gray-300',
			cellStyle:   {
				borderRightColor:  'transparent',
				borderBottomColor: 'transparent',
				borderLeftColor:   'transparent',
				overflow:          'visible',
				borderRightWidth:  0,
				borderTopWidth:    0,
				borderBottomWidth: 0,
				borderLeftWidth:   0,
				boxShadow:         'none',
				padding:           0,
			},
		},
		globalLevel: {
			color:   'hsl(var(--gray-300))',
			anchors: {
				color:         'hsl(var(--gray-600))',
				firstColumnId: 'impressions',
				label:         'METRICS',
			},
			columns: [
				{
					field:           'index',
					cellRenderer:    IndexCellRenderer,
					headerComponent: IndexHeaderRenderer,
					headerName:      '#',
					sortable:        false,
					pinned:          true,
					lockPosition:    true,
					headerClass:     () => [
						'border-r-gray-300',
						'border-r-[1px]',
						'border-b-[1px]',
						'border-b-gray-300',
						'flex',
						'justify-center',
					],
					width:    36,
					minWidth: 36,
					maxWidth: 36,
				},
				{
					colId:              'status',
					field:              'status',
					sortable:           true,
					cellRenderer:       StatusCellRenderer,
					headerComponent:    CustomHeader,
					cellRendererParams: {
						skipCellRendererWrapper: true,
					},
					valueSetter:	params => {
						params.data.statusPending = params.newValue;

						return true;
					},
					pinned:          'left',
					lockPosition:    true,
					suppressMovable: true,
					headerName:      'Live',
					width:           48,
					minWidth:        48,
					maxWidth:        48,
				},
				{
					columnMode:           ColumnModeTypeEnum.REGULAR,
					field:                'clientPinnedSettings',
					headerComponent:      ClientSettingsHeaderRenderer,
					pinned:               'left',
					maxWidth:             36,
					minWidth:             36,
					cellRendererSelector: params => {
						if (params.node?.data?.level === RedditLevel.AD_ACCOUNT) {
							return {
								component: ClientSettingsCellRenderer,
								params:    {
									...params,
									columnOrderIndex: 1,
								},
							};
						}

						return {
							component: AdAccountSettingsCellRenderer,
							params:    {
								...params,
								columnOrderIndex: 1,
							},
						};
					},
				},
				{
					field:                'settings',
					pinned:               'left',
					elasticParent:        'settingsElastic',
					headerComponent:      SettingsHeader,
					minWidth:             36,
					maxWidth:             36,
					cellRendererSelector: params => {
						if ([RedditLevel.AD_ACCOUNT].includes(params.node?.data?.level)) {
							return {
								component: AdAccountSettingsCellRenderer,
								params:    {
									...params,
									columnOrderIndex: 2,
								},
							};
						}

						return {
							component: SettingsCellRenderer,
							params:    {
								...params,
								columnOrderIndex: 2,
							},
						};
					},
				},
				{
					field:           'impressions',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
				},
				{
					field:           'clicks',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
				},
				{
					field:              'spend',
					cellRenderer:       StatCellRenderer,
					cellRendererParams: (params: ICellRendererParams) => ({
						currency:        params.data.currency,
						currencyDivisor: divideCurrencyFields.spend,
						fractionDigits:  2,
					}),
					suppressMovable: true,
				},
				{
					field:              'ecpm',
					headerName:         'eCPM (Effective cost per mille)',
					cellRenderer:       StatCellRenderer,
					cellRendererParams: (params: ICellRendererParams) => ({
						currency:        params.data.currency,
						currencyDivisor: divideCurrencyFields.spend,
						fractionDigits:  2,
					}),
					suppressMovable: true,
				},
				{
					field:              'cpc',
					headerName:         'CPC (Cost per click)',
					cellRenderer:       StatCellRenderer,
					cellRendererParams: (params: ICellRendererParams) => ({
						currency:        params.data.currency,
						currencyDivisor: divideCurrencyFields.spend,
						fractionDigits:  2,
					}),
					suppressMovable: true,
				},
				{
					field:              'ctr',
					headerName:         'CTR (Click-through rate)',
					cellRenderer:       StatCellRenderer,
					cellRendererParams: {
						isRate:         true,
						fractionDigits: 2,
					},
					suppressMovable: true,
				},
				{ field: 'appInstallMetricsAddPaymentInfo', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'appInstallMetricsAddToCart', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'appInstallMetricsAppLaunch', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'appInstallMetricsCompletedTutorial', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'appInstallMetricsInstall', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'appInstallMetricsLevelAchieved', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'appInstallMetricsPurchase', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'appInstallMetricsSearch', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'appInstallMetricsSignUp', cellRenderer: StatCellRenderer, suppressMovable: true },
				{
					field:              'appInstallMetricsSpend',
					cellRenderer:       StatCellRenderer,
					cellRendererParams: (params: ICellRendererParams) => ({
						currency:        params.data.currency,
						currencyDivisor: divideCurrencyFields.spend,
						fractionDigits:  2,
					}),
					suppressMovable: true,
				},
				{ field: 'appInstallMetricsSpendCredits', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'appInstallMetricsViewContent', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'commentDownvotes', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'commentUpvotes', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'commentsPageViews', cellRenderer: StatCellRenderer, suppressMovable: true },
				{
					field:              'conversionAddToCartAvgValue',
					cellRenderer:       StatCellRenderer,
					cellRendererParams: (params: ICellRendererParams) => ({
						currency:        params.data.currency,
						currencyDivisor: divideCurrencyFields.spend,
						fractionDigits:  2,
					}),
					suppressMovable: true,
				},
				{ field: 'conversionAddToCartTotalItems', cellRenderer: StatCellRenderer, suppressMovable: true },
				{
					field:              'conversionAddToCartTotalValue',
					cellRenderer:       StatCellRenderer,
					cellRendererParams: (params: ICellRendererParams) => ({
						currency:        params.data.currency,
						currencyDivisor: divideCurrencyFields.spend,
						fractionDigits:  2,
					}),
					suppressMovable: true,
				},
				{
					field:              'conversionAddToWishlistAvgValue',
					cellRenderer:       StatCellRenderer,
					cellRendererParams: (params: ICellRendererParams) => ({
						currency:        params.data.currency,
						currencyDivisor: divideCurrencyFields.spend,
						fractionDigits:  2,
					}),
					suppressMovable: true,
				},
				{ field: 'conversionAddToWishlistTotalItems', cellRenderer: StatCellRenderer, suppressMovable: true },
				{
					field:              'conversionAddToWishlistTotalValue',
					cellRenderer:       StatCellRenderer,
					cellRendererParams: (params: ICellRendererParams) => ({
						currency:        params.data.currency,
						currencyDivisor: divideCurrencyFields.spend,
						fractionDigits:  2,
					}),
					suppressMovable: true,
				},
				{
					field:              'conversionLeadTotalValue',
					cellRenderer:       StatCellRenderer,
					cellRendererParams: (params: ICellRendererParams) => ({
						currency:        params.data.currency,
						currencyDivisor: divideCurrencyFields.spend,
						fractionDigits:  2,
					}),
					suppressMovable: true,
				},
				{
					field:              'conversionPurchaseAvgValue',
					cellRenderer:       StatCellRenderer,
					cellRendererParams: (params: ICellRendererParams) => ({
						currency:        params.data.currency,
						currencyDivisor: divideCurrencyFields.spend,
						fractionDigits:  2,
					}),
					suppressMovable: true,
				},
				{ field: 'conversionPurchaseTotalItems', cellRenderer: StatCellRenderer },
				{
					field:              'conversionPurchaseTotalValue',
					cellRenderer:       StatCellRenderer,
					cellRendererParams: (params: ICellRendererParams) => ({
						currency:        params.data.currency,
						currencyDivisor: divideCurrencyFields.spend,
						fractionDigits:  2,
					}),
					suppressMovable: true,
				},
				{
					field:              'conversionSignupTotalValue',
					cellRenderer:       StatCellRenderer,
					cellRendererParams: (params: ICellRendererParams) => ({
						currency:        params.data.currency,
						currencyDivisor: divideCurrencyFields.spend,
						fractionDigits:  2,
					}),
					suppressMovable: true,
				},
				{ field: 'conversionRoas', cellRenderer: StatCellRenderer },
				{
					field:           'customClickThroughConversionsAttributionWindowDay',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
				},
				{
					field:           'customClickThroughConversionsAttributionWindowMonth',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
				},
				{
					field:           'customClickThroughConversionsAttributionWindowWeek',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
				},
				{
					field:           'customViewThroughConversionsAttributionWindowDay',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
				},
				{
					field:           'customViewThroughConversionsAttributionWindowMonth',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
				},
				{
					field:           'customViewThroughConversionsAttributionWindowWeek',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
				},
				{ field: 'videoFullyViewableImpressions', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'videoPlaysExpanded', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'videoPlaysWithSound', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'videoStarted', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'videoViewableImpressions', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'videoWatched100Percent', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'videoWatched10Seconds', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'videoWatched25Percent', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'videoWatched3Seconds', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'videoWatched50Percent', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'videoWatched5Seconds', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'videoWatched75Percent', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'videoWatched95Percent', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'viewableImpressions', cellRenderer: StatCellRenderer, suppressMovable: true },
				{ field: 'viewerComments', cellRenderer: StatCellRenderer, suppressMovable: true },
			],
		},
		levels: [
			{
				level:      RedditLevel.AD_ACCOUNT,
				color:      RedditColors.AD_ACCOUNT,
				nameColDef: {
					...NAME_COL_DEF,
					colId:                 'adAccountName',
					editable:              false,
					suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => (params.event?.target as HTMLElement)?.tagName?.toLowerCase?.() === 'input',
				},
				anchors: {
					color:          'hsl(var(--blue-500))',
					secondaryColor: 'hsl(var(--partner-50))',
					firstColumnId:  'clientId',
					label:          'AD ACCOUNTS',
				},
				columns: AD_ACCOUNT_COL_DEF,
			},
			{
				level:      RedditLevel.CAMPAIGN,
				color:      RedditColors.CAMPAIGN,
				nameColDef: {
					...NAME_COL_DEF,
					colId:    'campaignName',
					editable: editableOnLevel(RedditLevel.CAMPAIGN),
				},
				anchors: {
					color:          'hsl(var(--campaign-600))',
					secondaryColor: 'hsl(var(--campaign-50))',
					label:          'CAMPAIGNS',
				},
				columns: [
					{
						field:        'id',
						columnMode:   ColumnModeTypeEnum.REGULAR,
						rowGroup:     true,
						hide:         true,
						elasticChild: 'settingsElastic',
					},
					{
						colId:              'campaignObjective',
						field:              'campaignObjective',
						headerName:         campaignHeaderNames.campaignObjective,
						elasticChild:       'settingsElastic',
						columnMode:         ColumnModeTypeEnum.BOTH,
						editable:           params => editableOnThisLevel(params) && params.data.isInCreation,
						cellRenderer:       SelectCellRenderer,
						cellRendererParams: params => ({
							disabled: params?.data?.isCreate,
							options:  CAMPAIGN_OBJECTIVE_OPTIONS,
						}),
						cellEditor:            SelectCellEditor,
						suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => params.editing,
						valueSetter:           (params: ValueSetterParams) => {
							if (!Object.values(CampaignObjective).includes(params.newValue)) {
								return false;
							}

							params.data.campaignObjective = params.newValue;

							return true;
						},
						cellEditorParams: {
							options: CAMPAIGN_OBJECTIVE_OPTIONS,
						},
					},
					{
						field:                 'fundingInstrument',
						colId:                 'fundingInstrument',
						elasticChild:          'settingsElastic',
						headerName:            campaignHeaderNames.fundingInstrument,
						columnMode:            ColumnModeTypeEnum.BOTH,
						width:                 233,
						editable:              editableOnThisLevel,
						cellRenderer:          SelectCellRenderer,
						cellEditor:            SelectCellEditor,
						suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => params.editing,
						cellEditorParams:      params => ({
							options: getFundingInstrumentOptions(params.data),
						}),
						cellRendererParams: params => ({
							disabled: params?.data?.isCreate,
							options:  getFundingInstrumentOptions(params.data),
						}),
					},
					{
						field:            'campaignSpendCap',
						colId:            'campaignSpendCap',
						elasticChild:     'settingsElastic',
						headerName:       campaignHeaderNames.campaignSpendCap,
						columnMode:       ColumnModeTypeEnum.BOTH,
						editable:         editableOnThisLevel,
						cellEditor:       MoneyCellEditor,
						cellEditorParams: (params) => ({
							divisor:         divideCurrencyFields.spend,
							derivedCurrency: params?.data?.currency ?? params.data?.parent?.currency,
							nullable:        true,
							placeholder:     'Unset',
						}),
						cellRenderer:       MoneyCellRenderer,
						cellRendererParams: params => ({
							disabled:    params?.data?.isCreate,
							divisor:     divideCurrencyFields.spend,
							placeholder: 'Unset',
						}),
					},
				],
			},
			{
				level:      RedditLevel.GROUP,
				color:      RedditColors.GROUP,
				nameColDef: {
					...NAME_COL_DEF,
					colId:      'groupName',
					headerName: groupHeaderNames.groupName,
					editable:   editableOnLevel(RedditLevel.GROUP),
				},
				anchors: {
					color:          'hsl(var(--group-600))',
					secondaryColor: 'hsl(var(--group-50))',
					label:          'GROUPS',
					subAnchors:     [
						{ colId: 'groupBidStrategy', label: 'Setup' },
						{ colId: 'groupBidAmount', label: 'Delivery' },
						{ colId: 'placements', label: 'Placements' },
						{ colId: 'communities', label: 'Audience' },
						{ colId: 'locations', label: 'Demographics' },
						{ colId: 'deviceType', label: 'Devices' },
					],
				},
				columns: GROUP_COLUMN_DEFINITIONS,
			},
			{
				level:      RedditLevel.AD,
				color:      RedditColors.AD,
				nameColDef: {
					...NAME_COL_DEF,
					colId:       'adName',
					headerName:  adHeaderNames.adName,
					editable:    editableOnLevel(RedditLevel.AD),
					maxWidth:    36,
					headerClass: params => (
						params.context?.gridTopLevel === RedditLevel.AD
							? ['border-gray-300 border-b !overflow-visible border-l !w-auto']
							: ['border-gray-300 border-b !overflow-visible']
					),
				},
				anchors: {
					color:          'hsl(var(--ad-600))',
					secondaryColor: 'hsl(var(--ad-50))',
					label:          'ADS',
					subAnchors:     [
						{ colId: 'creativeType', label: 'Setup' },
						{ colId: 'adClickEventTrackers', label: 'Trackers' },
					],
				},
				columns: AD_COLUMN_DEFINITIONS,
			},
			{
				level:   RedditLevel.BREAKDOWN,
				color:   'hsl(var(--gray-300))',
				anchors: {
					firstColumnId: 'status',
					label:         'BREAKDOWN',
					color:         'hsl(var(--gray-600))',
				},
				nameColDef: {
					...NAME_COL_DEF,
					colId:       'breakdownName',
					headerName:  adHeaderNames.adName,
					editable:    editableOnLevel(RedditLevel.BREAKDOWN),
					minWidth:    0,
					width:       document.body.clientWidth / 4,
					maxWidth:    800,
					headerClass: 'border-gray-300 border-b border-r',
				},
				columns: [],
			},
		],
	})
	.getConfig();
